import React, { useState, useEffect } from 'react';
import axios from 'axios';
import Container from '@mui/material/Container';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import CircularProgress from '@mui/material/CircularProgress';
import Box from '@mui/material/Box';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Stack from '@mui/material/Stack';
import Select from '@mui/material/Select';
import MenuItem from '@mui/material/MenuItem';
import FormControl from '@mui/material/FormControl';
import InputLabel from '@mui/material/InputLabel';
import { v4 as uuidv4 } from 'uuid';
import showdown from 'showdown';
import { DataGrid } from '@mui/x-data-grid';
import IconButton from '@mui/material/IconButton';
import VisibilityIcon from '@mui/icons-material/Visibility';
import Dialog from '@mui/material/Dialog';
import DialogTitle from '@mui/material/DialogTitle';
import DialogContent from '@mui/material/DialogContent';
import DialogActions from '@mui/material/DialogActions';
import EditOutlinedIcon from '@mui/icons-material/EditOutlined';
import { useNavigate } from 'react-router-dom';

const baseApiUri = process.env.REACT_APP_API_URL;
const converter = new showdown.Converter();

function sleep(ms) {
  return new Promise(resolve => setTimeout(resolve, ms));
}

export default function Assessments() {
  const [file, setFile] = useState(null);
  const [fileUrl, setFileUrl] = useState('');
  const [status, setStatus] = useState('idle');
  const [assessmentHtml, setAssessmentHtml] = useState(null);
  const [error, setError] = useState(null);
  const [requestSortKey, setRequestSortKey] = useState(0);

  const navigate = useNavigate();

  // Table state
  const [loading, setLoading] = useState(false);
  const [rows, setRows] = useState([]);
  const [assessmentsMap, setAssessmentsMap] = useState({});

  // Modal state
  const [selectedAssessment, setSelectedAssessment] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);

  // NEW: Toggle state for uploader visibility
  const [showUploader, setShowUploader] = useState(true);

  // NEW: Assistant selection state
  const [assistants, setAssistants] = useState([]);
  const [selectedAssistantId, setSelectedAssistantId] = useState('');

  const columns = [
    { field: 'id', headerName: 'ID', flex: 1 },
    { field: 'title', headerName: 'Title', flex: 2 },
    { field: 'date', headerName: 'Date Created', flex: 2 },
    {
      field: 'view',
      headerName: 'View',
      flex: 1,
      renderCell: ({ row }) => (
        <IconButton onClick={() => handleViewAssessment(row.id)} color="primary" aria-label={`View ${row.title || 'assessment'}`}>
          <VisibilityIcon />
        </IconButton>
      )
    },
    {
  field: 'edit',
  headerName: 'Edit',
  flex: 1,
  renderCell: ({ row }) => (
    <IconButton
      onClick={() => navigate(`/assessments-edit/${row.id}`)}
      aria-label={`Edit ${row.title || 'assessment'}`}
    >
      <EditOutlinedIcon />
    </IconButton>
  )
}
  ];

  /** ✅ Load assessments with title attribute */
  const loadAssessments = async () => {
    setLoading(true);
    try {
      const endpoint = `${baseApiUri}/ai/completions?type=needs-assessment&fields=query,fileName,created,message,status,title`;
      const res = await axios.get(endpoint);

      console.log('RES',res.data);

      const filtered = res.data.completions
        .filter(c => c.status === 'success')
        .sort((a, b) => new Date(b.created) - new Date(a.created));

      const formattedRows = filtered.map(c => ({
        id: c.id,
        title: c.title || '',
        date: new Date(c.created).toLocaleString()
      }));

      const messageMap = {};
      filtered.forEach(c => {
        messageMap[c.id] = c.message;
      });
      console.log('ROWS',formattedRows);
      setRows(formattedRows);
      setAssessmentsMap(messageMap);
    } catch (err) {
      console.error('Error loading assessments:', err);
    } finally {
      setLoading(false);
    }
  };

  /** NEW: Load assistants (similar to AssistantsAdmin.js) */
  const loadAssistants = async () => {
    try {
      const endpoint = `${baseApiUri}/ai/assistant?key=09848734-8745-afrt-8745-8745873487`;
      const res = await axios.get(endpoint);
      const filtered = res.data.assistants.filter(a => a.sandbox == null);
      setAssistants(filtered);
    } catch (error) {
      console.error('Error loading assistants:', error);
    }
  };

  /** ✅ Open modal with HTML-rendered message and dynamic title */
  const handleViewAssessment = (id) => {
    const row = rows.find(r => r.id === id);
    console.log('VIEW', row);
    setSelectedAssessment({
      id,
      message: assessmentsMap[id],
      title: row?.title || 'Assessment Details'
    });
    setModalOpen(true);
  };

  const handleCloseModal = () => setModalOpen(false);

  /** NEW: Get selected assistant name for dynamic text */
  const getSelectedAssistantName = () => {
    if (!selectedAssistantId) return 'Needs Assessment';
    const assistant = assistants.find(a => a.id === selectedAssistantId);
    return assistant ? assistant.name : 'Needs Assessment';
  };

  useEffect(() => {
    loadAssessments();
    loadAssistants();
  }, []);

  /** File upload logic */
  const handleFileChange = (e) => {
    setFile(e.target.files[0]);
    setAssessmentHtml(null);
    setStatus('idle');
    setError(null);
  };

  const handleSubmit = async () => {
    if (!file && !fileUrl.trim()) return;
    if (!selectedAssistantId) {
      setError('Please select an assistant.');
      return;
    }

    setStatus('uploading');
    setError(null);

    try {
      // First, fetch the selected assistant (similar to loadAssistant from AssistantAdmin.js)
      const assistantEndpoint = `${baseApiUri}/ai/assistant/${selectedAssistantId}?key=09848734-8745-afrt-8745-8745873487`;
      const assistantRes = await axios.get(assistantEndpoint);
      const assistant = assistantRes.data.assistant;

      const formData = new FormData();
      const generatedId = uuidv4();

      formData.append('key', '09848734-8745-afrt-8745-8745873487');
      formData.append('sortKey', parseInt(requestSortKey));
      formData.append('query', 'Generate a needs assessment from this document.');
      // Use the assistant's type instead of hardcoded 'needs-assessment'
      formData.append('type', assistant.type || 'needs-assessment');
      formData.append('stream', 'true');
      formData.append('id', generatedId);

      if (file) formData.append('filename', file);
      if (fileUrl.trim()) formData.append('fileurl', fileUrl.trim());

      const response = await axios.post(`${baseApiUri}/ai/completion`, formData, {
        withCredentials: true,
        headers: { 'Content-Type': 'multipart/form-data' }
      });

      const { id, sortKey } = response.data;
      setStatus('polling');
      pollForAssistantResponse(id, sortKey);
    } catch (err) {
      console.error(err);
      setStatus('error');
      setError('Failed to submit the file.');
    }
  };

  /** ✅ Append new assessment to table when successful */
  const pollForAssistantResponse = async (id, sortKey) => {
    let hasMore = true;

    const poll = async () => {
      try {
        while (hasMore) {
          const endpoint = `${baseApiUri}/ai/completion/${id}/${sortKey}`;
          const completion = await axios.get(endpoint);
          const { status: compStatus, message } = completion.data.response;

          if (compStatus === 'success') {
            const finalHtml = converter.makeHtml(message);
            setAssessmentHtml(finalHtml);
            setStatus('completed');
            setRequestSortKey(prev => prev + 1);

            // ✅ Add new successful assessment to table + message map (title may be empty)
            const newRow = { id, title: '', date: new Date().toLocaleString() };
            setRows(prev => [newRow, ...prev]);
            setAssessmentsMap(prev => ({ ...prev, [id]: message }));

            hasMore = false;
          }

          await sleep(500);
        }
      } catch (error) {
        console.error('Polling failed:', error);
      }
    };

    poll();
  };

  // Render
  return (
    <Container maxWidth="xl" sx={{ padding: '2rem' }}>
      {/* Main horizontal layout:
          [Toggle Rail] [Uploader (conditional)]    [Assessments Table (flexes to fill)] */}
      <Box sx={{ display: 'flex', alignItems: 'stretch' }}>
        {/* Toggle Rail — always visible and sits immediately left of the uploader.
            Shows '<<' when uploader is visible (to hide it), and '>>' when hidden (to show it). */}
        <Box
          role="button"
          aria-label={showUploader ? 'Hide upload panel' : 'Show upload panel'}
          title={showUploader ? 'Hide upload panel' : 'Show upload panel'}
          tabIndex={0}
          onClick={() => setShowUploader(prev => !prev)}
          onKeyDown={(e) => {
            if (e.key === 'Enter' || e.key === ' ') {
              e.preventDefault();
              setShowUploader(prev => !prev);
            }
          }}
          sx={{
            width: 28,
            mr: 2, // space between rail and the next column (uploader or table)
            alignSelf: 'stretch',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            bgcolor: 'grey.100',
            color: 'text.secondary',
            borderRadius: 1,
            border: '1px solid',
            borderColor: 'divider',
            cursor: 'pointer',
            userSelect: 'none',
            '&:hover': { bgcolor: 'grey.200' },
            '&:focus': { outline: '0px solid', outlineColor: 'primary.main' }
          }}
        >
          {showUploader ? '<<' : '>>'}
        </Box>

        {/* Left Column: Uploader (conditionally rendered) */}
        {showUploader && (
          <Box sx={{ width: { xs: '100%', md: '42%' }, flexShrink: 0, mr: { xs: 0, md: 4 } }}>
            <Paper sx={{ padding: '2rem', height: '100%' }} elevation={3}>
              

              <Stack spacing={2}>
                {/* NEW: Assistant selection dropdown */}
                <FormControl fullWidth>
                  <InputLabel id="assistant-select-label">Select Assistant</InputLabel>
                  <Select
                    labelId="assistant-select-label"
                    id="assistant-select"
                    value={selectedAssistantId}
                    label="Select Assistant"
                    onChange={(e) => setSelectedAssistantId(e.target.value)}
                  >
                    {assistants.map((assistant) => (
                      <MenuItem key={assistant.id} value={assistant.id}>
                        {assistant.name}
                      </MenuItem>
                    ))}
                  </Select>
                </FormControl>

<Typography variant="h5" gutterBottom>Upload a File for {getSelectedAssistantName()}</Typography>

                <TextField type="file" onChange={handleFileChange} inputProps={{ accept: '.pdf,.doc,.docx,.txt,.md' }} fullWidth />
                <Typography variant="body2" color="text.secondary">
                  If your attachment is larger than 10 MB, please provide a URL to your document instead:
                </Typography>
                <TextField
                  label="Document URL"
                  variant="outlined"
                  fullWidth
                  value={fileUrl}
                  onChange={(e) => setFileUrl(e.target.value)}
                  placeholder="https://example.com/my-large-document.pdf"
                />
                <Button variant="contained" onClick={handleSubmit} disabled={(status === 'uploading' || (!file && !fileUrl) || !selectedAssistantId)}>
                  Generate Assessment
                </Button>
              </Stack>

              {status === 'uploading' && (
                <Box mt={2}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 1, display: 'inline' }}>Uploading file...</Typography>
                </Box>
              )}
              {status === 'polling' && (
                <Box mt={2}>
                  <CircularProgress size={24} />
                  <Typography variant="body2" sx={{ ml: 1, display: 'inline' }}>Generating assessment, please wait...</Typography>
                </Box>
              )}
              {status === 'error' && (
                <Box mt={2}>
                  <Typography variant="body2" color="error">{error}</Typography>
                </Box>
              )}
              {status === 'completed' && assessmentHtml && (
                <Box mt={4} sx={{ border: '1px solid #ccc', padding: '1rem' }}>
                  <Typography variant="h6" gutterBottom>✅ Assessment Complete</Typography>
                  <div dangerouslySetInnerHTML={{ __html: assessmentHtml }} />
                </Box>
              )}
            </Paper>
          </Box>
        )}

        {/* Right Column: Assessments Table (expands to full width when uploader hidden) */}
        <Box sx={{ flex: 1 }}>
          <Paper sx={{ padding: '1rem' }} elevation={3}>
            <Typography variant="h5" gutterBottom>Current Assessments</Typography>
            <Box sx={{ height: 500, width: '100%', backgroundColor: '#fff' }}>
              <DataGrid
                rows={rows}
                columns={columns}
                loading={loading}
                initialState={{
                  pagination: { paginationModel: { pageSize: 5 } }
                }}
                pageSizeOptions={[5]}
                disableRowSelectionOnClick
              />
            </Box>
          </Paper>
        </Box>
      </Box>

      {/* ✅ Modal with HTML-rendered assessment message and big title */}
      <Dialog open={modalOpen} onClose={handleCloseModal} maxWidth="md" fullWidth>
        <DialogTitle>
          <Typography variant="h3" component="h2">
            {selectedAssessment?.title || 'Assessment Details'}
          </Typography>
        </DialogTitle>
        <DialogContent dividers>
          <div
            dangerouslySetInnerHTML={{
              __html: converter.makeHtml(selectedAssessment?.message || 'No message available.')
            }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseModal}>Close</Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
}
